import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { Injectable } from '@angular/core';
import * as contracts from '../contract/email';
import { GetEmailByIdRequest, GetEmailByIdResponse } from '../contract/email';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EmailMapper } from '../mappers/email-mapper';
import { PaginationInput } from '../../../../shared/graphql/sourcing.types';
import { CollectionNameEnum, EmailNotificationDocument } from '../../../models';

@Injectable()
export class EmailService implements contracts.IEmailService {
  apollo: ApolloBase;

  constructor(private apolloProvider: Apollo) {
    this.apollo = this.apolloProvider.use('emailApollo');
  }

  getEmailList(request: contracts.GetEmailListRequest): Observable<contracts.GetEmailListResponse> {
    const { lookup } = request;

    const documents: Array<EmailNotificationDocument> = [];
    if (lookup.aircraft) {
      documents.push(
        new EmailNotificationDocument()
          .setDocumentId(lookup.aircraft)
          .setCollection(CollectionNameEnum.AIRCRAFT)
      );
    }

    if (lookup.operator) {
      documents.push(
        new EmailNotificationDocument()
          .setDocumentId(lookup.operator)
          .setCollection(CollectionNameEnum.OPERATOR)
      );
    }

    if (lookup.user) {
      documents.push(
        new EmailNotificationDocument()
          .setDocumentId(lookup.user)
          .setCollection(CollectionNameEnum.USER)
      );
    }

    if (lookup.operatorPurchaseOffer) {
      documents.push(
        new EmailNotificationDocument()
          .setDocumentId(lookup.operatorPurchaseOffer)
          .setCollection(CollectionNameEnum.OPERATOR_PURCHASE_OFFER)
      );
    }

    if (lookup.sourcingRequest) {
      documents.push(
        new EmailNotificationDocument()
          .setDocumentId(lookup.sourcingRequest)
          .setCollection(CollectionNameEnum.SOURCING_REQUEST)
      );
    }

    const filter: any = {
      ...(lookup.id ? { id: { eq: lookup.id } } : {}),
      ...(lookup.from ? { from: { contains: lookup.from } } : {}),
      ...(lookup.to ? { to: { contains: lookup.to } } : {}),
      ...(lookup.cc ? { cc: { contains: lookup.cc } } : {}),
      ...(lookup.bcc ? { bcc: { contains: lookup.bcc } } : {}),
      ...(lookup.template?.length ? { template: templateFilter(lookup.template) } : {}),
      ...(lookup.subject ? { subject: { contains: lookup.subject } } : {}),
      ...(lookup.replyTo ? { replyTo: { contains: lookup.replyTo } } : {}),
      ...(lookup.skipSending !== null && lookup.skipSending !== undefined
        ? lookup.skipSending
          ? { skipSending: { eq: true } }
          : { skipSending: { neq: true } }
        : {}),
      ...(documents?.length
        ? {
            documents: documents.map((doc) => ({
              collection: { eq: doc.collection },
              documentId: { eq: doc.documentId },
            })),
          }
        : {}),
    };

    if (lookup.fromDateTime || lookup.toDateTime) {
      filter.sent = {
        ...(lookup.fromDateTime ? { gte: lookup.fromDateTime.toISOString() } : {}),
        ...(lookup.toDateTime ? { lte: lookup.toDateTime.toISOString() } : {}),
      };
    }

    const pagination: PaginationInput = {
      skip: lookup.skip,
      limit: lookup.limit,
    };

    const sorting: any = {};
    if (lookup.sort && lookup.sort.length) {
      const [sortDescriptor] = lookup.sort;
      sorting[sortDescriptor.field] = sortDescriptor.dir === 'asc';
    }

    return this.apollo
      .query<any>({
        query:
          gql(`query findEmailNotifications($filter: EmailNotificationFilter, $sorting: EmailNotificationSorting, $pagination: PaginationInput!) {
            findEmailNotifications(filter: $filter, sorting: $sorting, pagination : $pagination) {
              items {
                id
                skipSending
                from
                to
                cc
                bcc
                repliesTo
                subject
                sent
                emailAttachments{
                  name
                }
              }
              totalElements
            }
          }`),
        variables: {
          filter,
          sorting,
          pagination,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((svcJsonResp) => {
          const response = new contracts.GetEmailListResponse();

          if (!svcJsonResp?.data?.findEmailNotifications) {
            return response;
          } else {
            response.entities = svcJsonResp.data.findEmailNotifications.items.map((item) =>
              EmailMapper.Instance().emailDtoToEmail(item)
            );
            response.totalItems = svcJsonResp.data.findEmailNotifications.totalElements;
          }
          return response;
        })
      );
  }

  getEmailById(request: GetEmailByIdRequest): Observable<GetEmailByIdResponse> {
    return this.apollo
      .query<any>({
        query: gql(`query getEmailNotificationBody($id: String!){
        getEmailNotification(id: $id) {
          id
          originalFrom
          originalTo
          originalCc
          originalBcc
          template
          document {
            collection
            documentId
          }
          subject
          emailAttachments{
            name
          }
          emailNotificationHistories{
            comment
            eventType
            timestamp
            recipients
            userAgent
            ipAddress
            bouncedRecipients{
              action
              diagnosticCode
            }
          }
        }
      }`),
        variables: {
          id: request.emailId,
        },
      })
      .pipe(
        map((svcJsonResp) => {
          const response = new contracts.GetEmailByIdResponse();

          if (!svcJsonResp?.data?.getEmailNotification) {
            return response;
          } else {
            response.email = EmailMapper.Instance().emailDtoToEmail(
              svcJsonResp.data.getEmailNotification
            );
          }
          return response;
        })
      );
  }

  getEmailBody(request: contracts.GetEmailBodyRequest): Observable<contracts.GetEmailBodyResponse> {
    return this.apollo
      .query<any>({
        query: gql(`query getEmailNotificationBody($id: String!){
        getEmailNotification(id: $id) {
          body
        }
      }`),
        variables: {
          id: request.emailId,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((svcJsonResp) => {
          const response = new contracts.GetEmailBodyResponse();

          if (!svcJsonResp?.data?.getEmailNotification) {
            return response;
          } else {
            response.body = svcJsonResp.data.getEmailNotification.body;
          }
          return response;
        })
      );
  }
}

// One template still matches on a substring, which is what the free-text filter offers; several can
// only be an exact set, or a short name would pull in every template it is a prefix of.
function templateFilter(templates: Array<string>): Record<string, unknown> {
  return templates.length === 1 ? { contains: templates[0] } : { in: [...templates] };
}
